import React, { useEffect, useState, useMemo, useRef, KeyboardEvent, useCallback } from "react"; // Added useCallback
import { SyncInjectorNodeData, HandleConfig } from "../../types"; // Added HandleConfig
import { Position } from "reactflow"; // Added Position
import { useIntegrations } from "../../hooks/useIntegrations";
import { Search, X, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import AutoComplete from "../../components/shared/AutoComplete";
import HandleManager from "../../components/shared/HandleManager"; // Import HandleManager

interface SyncInjectorConfigBoxProps {
  nodeId: string;
  data: SyncInjectorNodeData;
  onUpdate?: (data: Partial<SyncInjectorNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void; // Add prop
  isAdvancedMode?: boolean;
  portalRoot?: HTMLElement | null;
}

const CUSTOM_LABEL_SUGGESTIONS = [
  { value: "Production", label: "Production" },
  { value: "Development", label: "Development" },
  { value: "Staging", label: "Staging" },
  { value: "Enterprise", label: "Enterprise" },
  { value: "Beta", label: "Beta" },
  { value: "Internal", label: "Internal" },
  { value: "High Priority", label: "High Priority" },
  { value: "Low Priority", label: "Low Priority" },
  { value: "Critical", label: "Critical" },
  { value: "Experimental", label: "Experimental" },
  { value: "Legacy", label: "Legacy" },
  { value: "New", label: "New" },
  { value: "Future State", label: "Future State" },
];

// --- Default Handles for Sync Injector ---
const defaultHandles: HandleConfig[] = [
  // Sync Injector typically only has a source handle, but let's define both for consistency if needed
  { id: 'default-source', type: 'source', position: Position.Right, color: '#6366F1' }, // Indigo Source
  // { id: 'default-target', type: 'target', position: Position.Left, color: '#3B82F6' }, // Blue Target (Optional)
];
// --- End Default Handles ---

export default function SyncInjectorConfigBox({
  data,
  nodeId,
  onUpdate,
  onDelete,
  onDuplicate,
  onDetachFromGroup, // Destructure prop
  isAdvancedMode = false,
  portalRoot,
}: SyncInjectorConfigBoxProps) {
  // Initialize state with props
  const [label, setLabel] = useState(data.label || "");
  const [idSyncs, setIdSyncs] = useState(data.idSyncs || []);
  const [crossDomain, setCrossDomain] = useState(data.crossDomain || false);
  const [domains, setDomains] = useState(data.domains || []);
  const [searchQuery, setSearchQuery] = useState("");
  const [customLabel, setCustomLabel] = useState(data.customLabel || "");
  
  // Visibility settings
  const [idSyncsVisibility, setIdSyncsVisibility] = useState(data.idSyncsVisibility || "visible");
  const [crossDomainVisibility, setCrossDomainVisibility] = useState(data.crossDomainVisibility || "visible");
  const [customLabelVisibility, setCustomLabelVisibility] = useState<VisibilityOption>(
    data.customLabelVisibility === "hidden" ? "hidden" : "visible"
  );

  // --- Add State for Handles ---
  const [handles, setHandles] = useState<HandleConfig[]>(() =>
    Array.isArray(data.handles) ? [...data.handles] : defaultHandles
  );
  // --- End Add State for Handles ---


  const toggleCustomLabelVisibility = () => {
    setCustomLabelVisibility(prev => prev === "hidden" ? "visible" : "hidden");
  };

  // Track previous node ID to detect changes
  const prevNodeIdRef = useRef<string | null>(null);

  // Track previous update to prevent loops
  const prevUpdateRef = useRef("");
  // Track if component has initialized to prevent effect firing on mount
  const isInitialized = useRef(false);

  // Load integrations
  const { integrations, isLoading, loadIntegrations } = useIntegrations();
  
  // Initialize or update state from props when the node changes
  useEffect(() => {
    // Extract a unique identifier from the data to detect node changes
    const currentNodeId = (data as any).id || JSON.stringify(data);
    
    // If the node has changed or this is the first initialization
    if (prevNodeIdRef.current !== currentNodeId) {
      setLabel(data.label || "");
      setIdSyncs(data.idSyncs || []);
      setCrossDomain(data.crossDomain || false);
      setDomains(data.domains || []);
      setCustomLabel(data.customLabel || ""); // Sync custom label
      setIdSyncsVisibility(data.idSyncsVisibility || "visible");
      setCrossDomainVisibility(data.crossDomainVisibility || "visible");
      setCustomLabelVisibility(data.customLabelVisibility === "hidden" ? "hidden" : "visible"); // Sync custom label visibility

      // Sync handles state more carefully
      if (Array.isArray(data.handles) && JSON.stringify(data.handles) !== JSON.stringify(handles)) {
          setHandles([...data.handles]);
      }
      // Do not reset to defaultHandles here

      // Mark as initialized after the first sync
      if (!isInitialized.current) {
          isInitialized.current = true;
      }
    }
  }, [data]);

  // Load integrations on mount
  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);
  
  // Update parent when state changes
  useEffect(() => {
    // Prevent firing on initial mount/effect synchronization
    if (!isInitialized.current) {
      return;
    }

    const updates = {
      label,
      idSyncs,
      crossDomain,
      domains,
      idSyncsVisibility,
      crossDomainVisibility,
      customLabel,
      customLabelVisibility,
      handles: Array.isArray(handles) ? handles : [], // Add handles to update
    };

    const updatesJson = JSON.stringify(updates);
    if (updatesJson !== prevUpdateRef.current && onUpdate) {
      prevUpdateRef.current = updatesJson;
      onUpdate(updates);
    }
  }, [
      label,
      idSyncs,
      crossDomain,
      domains,
      idSyncsVisibility,
      crossDomainVisibility,
      customLabel,
      customLabelVisibility,
      handles, // Add handles dependency
      onUpdate
  ]);

  // Filter integrations based on search query and sync_injector support
  const filteredIntegrations = useMemo(() => {
    if (isLoading) return [];
    
    return integrations
      .filter(integration => integration.sync_injector === true) // Only include integrations that support sync injector
      .filter(integration => 
        integration.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
  }, [integrations, searchQuery, isLoading]);
  
  // Handle domain text changes
  const handleDomainsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newDomains = event.target.value
      .split("\n")
      .filter(domain => domain.trim() !== "");
    setDomains(newDomains);
  };
  
  // Toggle ID sync
  const handleIdSyncToggle = (id: string) => {
    setIdSyncs(prev => 
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  // Handle keyboard events for search field
  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setSearchQuery("");
    }
  };
  
  // Toggle visibility setting
  const toggleVisibility = (
    current: "hidden" | "visible",
    setter: React.Dispatch<React.SetStateAction<"hidden" | "visible">>
  ) => {
    const nextVisibility = current === "hidden" ? "visible" : "hidden";
    setter(nextVisibility);
  };

  // Render visibility icon based on current setting
  const renderVisibilityIcon = (visibility: string) => {
    return visibility === "hidden" 
      ? <EyeOff className="h-4 w-4 text-red-600 dark:text-red-400" />
      : <Eye className="h-4 w-4 text-gray-600 dark:text-gray-300" />;
  };

  // --- Handler for HandleManager changes ---
  const handleHandlesChange = useCallback((newHandles: HandleConfig[]) => {
    setHandles(newHandles);
  }, []);
  // --- End Handler ---

  return (
    <div className="space-y-3 relative">
      {/* Action toolbar */}
      <ConfigBoxToolbar
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass prop down
        isAdvancedMode={isAdvancedMode}
      />
      {/* Name field */}
      <div className="pt-3">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Name
        </label>
        <input
          type="text"
          value={label}
          onChange={e => setLabel(e.target.value)}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>


      {/* Custom Label field with visibility toggle */}
      <div>
        <LabelWithVisibilityToggle
          label="Custom Label"
          visibility={customLabelVisibility}
          onToggleVisibility={toggleCustomLabelVisibility}
          className="mb-1"
        />
        <AutoComplete
          value={customLabel}
          onChange={setCustomLabel}
          options={CUSTOM_LABEL_SUGGESTIONS}
          placeholder="Set a Custom Label"
          dropdownClassName="absolute z-[9999]"
          portalContainer={portalRoot}
        />
      </div>

      {/* External Vendor Identities */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-300">
            External Vendor Identities
          </h4>
          <button
            type="button"
            onClick={() => toggleVisibility(idSyncsVisibility, setIdSyncsVisibility)}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            title={idSyncsVisibility === "hidden" ? "Show in node" : "Hide in node"}
          >
            {renderVisibilityIcon(idSyncsVisibility)}
          </button>
        </div>
        <div
          className="border border-gray-300 dark:border-gray-600 rounded overflow-y-auto"
          style={{ height: "240px" }}
        >
          {/* Sticky search field */}
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-700 shadow-sm">
            <div className="relative">
              <input
                type="text"
                placeholder="Search integrations..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="w-full pl-8 pr-6 py-2 text-sm border-b border-gray-300 dark:border-gray-600 rounded-t focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
              {searchQuery && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-2 h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label="Clear search"
                >
                  <X />
                </motion.button>
              )}
            </div>
          </div>
          
          {/* Integration list */}
          {isLoading ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading...</div>
          ) : (
            filteredIntegrations.map(integration => (
              <motion.div
                key={integration.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
                onClick={() => handleIdSyncToggle(integration.id)}
                className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <div className="flex items-center w-full">
                  <motion.div
                    className="relative flex items-center justify-center mr-3"
                    whileTap={{ scale: 0.9 }}
                  >
                    <input
                      type="checkbox"
                      checked={idSyncs.includes(integration.id)}
                      onChange={() => {}} // Handled by the parent div onClick
                      className="sr-only" // Hide the actual checkbox
                    />
                    <motion.div
                      initial={false}
                      animate={{
                        backgroundColor: idSyncs.includes(integration.id) ? 'rgba(59, 130, 246, 1)' : 'rgba(59, 130, 246, 0)',
                        borderColor: idSyncs.includes(integration.id) ? 'rgb(59, 130, 246)' : 'rgb(209, 213, 219)'
                      }}
                      className="h-4 w-4 rounded border-2 flex items-center justify-center"
                    >
                      {idSyncs.includes(integration.id) && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                          className="text-white"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 11.5L11 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </motion.div>
                      )}
                    </motion.div>
                  </motion.div>
                  <img
                    src={integration.logo_url}
                    alt={integration.name}
                    className="h-4 w-4 mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {integration.name}
                  </span>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Cross-Domain section */}
      <div className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 rounded-md p-4">
        <div className="flex items-center justify-between mb-2">
          <label className="relative flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={crossDomain}
              onChange={() => setCrossDomain(!crossDomain)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-400 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-500/30 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-primary-500"></div>
            <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
              Cross-Domain Device Tracking
            </span>
          </label>
          <button
            type="button"
            onClick={() => toggleVisibility(crossDomainVisibility, setCrossDomainVisibility)}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            title={crossDomainVisibility === "hidden" ? "Show in node" : "Hide in node"}
          >
            {renderVisibilityIcon(crossDomainVisibility)}
          </button>
        </div>
        
        <p className="text-sm text-gray-700 dark:text-gray-400 mb-2">
          Allows you to maintain a consistent device identifier as a user
          browses multiple domains that you own.{" "}
          <a
            href="https://docs.metarouter.io/docs/cross-domain-identity-tracking"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-500 hover:text-primary-700"
          >
            Learn more
          </a>
        </p>

        {crossDomain && (
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Domains (one per line)
            </label>
            <textarea
              value={domains.join("\n")}
              onChange={handleDomainsChange}
              placeholder="example.com"
              rows={3}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        )}
      </div>

      {/* --- Render Handle Manager --- */}
      <div>
        <HandleManager
          nodeId={nodeId}
          handles={handles} // Use handles state
          onChange={handleHandlesChange}
          maxHandles={6} // Adjust as needed
          portalRoot={portalRoot}
        />
      </div>
      {/* --- End Handle Manager --- */}
    </div>
  );
}
